import { type AdInputField, getAt } from '@finn-no/mobility-item-insertion-shared';
import { Trans } from '@lingui/react/macro';
import { Alert, Button, Select, TextField } from '@warp-ds/react';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { ConditionWidgetModel } from '../../../types/widget.js';
import { getHuckConfig } from '../../utils/getHuckConfig.js';

type ConditionReportWidgetProps = Pick<ConditionWidgetModel, 'className' | 'mandatory'>;

type ConditionReportResponse = {
  reference: string;
  reportUrl: string;
  registrationNumber: string;
};

export const ConditionReportWidget = (props: ConditionReportWidgetProps) => {
  const config = getHuckConfig();
  const { register, setValue, watch, getValues } = useFormContext();
  const [inspectionId, setInspectionId] = useState<string>('');
  const [alert, setAlert] = useState<null | 'Error' | 'NotFound' | 'NoCode'>(null);
  const [loading, setLoading] = useState(false);
  const conditionReportProvider = watch('condition_report.provider');
  const conditionReportReference = watch('condition_report.reference');
  const registrationNumber = watch('registration_number');

  useEffect(() => {
    register('condition_report.reference');
    // Set initial value to undefined if it's an empty string
    if (getValues('condition_report.provider') === '') {
      setValue('condition_report.provider', undefined);
    }
  }, [register, getValues, setValue]);

  const conditionReportProviderField = getAt(
    config.adInputLookup,
    'condition_report.children.provider',
  ) as AdInputField;

  const conditionReportCodeLabel = getAt<string>(config.adInputLookup, 'condition_report.children.code.label');

  const providerOptions = useMemo(() => {
    if ('davs' in conditionReportProviderField) {
      return (
        conditionReportProviderField.davs?.values?.map((option) => (
          <option key={option.id} value={option.id}>
            {option.text}
          </option>
        )) ?? []
      );
    }

    return [];
  }, [conditionReportProviderField]);

  const fetchConditionReport = async () => {
    setAlert(null);
    setLoading(true);

    if (!registrationNumber) return;

    if (!inspectionId || inspectionId.trim() === '') {
      setAlert('NoCode');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${config.root}/api/condition-report/${registrationNumber}?provider=${conditionReportProvider}&inspectionId=${inspectionId.trim()}`,
      );

      if (!response.ok) {
        throw new Error('Failed to fetch condition report');
      }

      if (response.status === 200) {
        const data = (await response.json()) as ConditionReportResponse;
        setValue('condition_report.reference', data.reference);
      } else {
        setAlert('NotFound');
      }
    } catch (e) {
      setAlert('Error');
      console.error(e);
    } finally {
      setLoading(false);
      setTimeout(() => {
        setAlert(null);
      }, 5000);
    }
  };

  function handleProviderChange(event: React.ChangeEvent<HTMLSelectElement>) {
    const value = event.target.value === '' ? undefined : Number.parseInt(event.target.value, 10);
    setValue('condition_report.provider', value, { shouldDirty: true });
    setValue('condition_report.reference', undefined);
    setInspectionId('');
    setAlert(null);
  }

  function focusRegistrationNumber() {
    const element = document.querySelector('[data-attribute="registration_number"]') as HTMLElement;
    element?.focus();
  }

  return (
    <>
      <Select
        optional={!props.mandatory}
        label={conditionReportProviderField.label}
        always
        {...register('condition_report.provider', { valueAsNumber: true })}
        onChange={handleProviderChange}
        data-testid="condition_report.provider"
        className={props.className}
      >
        {props.mandatory ? providerOptions : [<option key="" value={undefined} />].concat(providerOptions)}
      </Select>

      {!!conditionReportProvider &&
        (!conditionReportReference ? (
          <div className={props.className}>
            <div className="flex gap-24 items-start">
              <TextField
                label={conditionReportCodeLabel}
                optional
                value={inspectionId}
                onChange={(event) => {
                  setAlert(null);
                  setInspectionId(event.currentTarget.value);
                }}
                invalid={alert === 'NoCode'}
                helpText={
                  alert === 'NoCode' ? (
                    <Trans id="mobility-item-insertion.form.condition-report.needs-code">
                      You need to enter an inspection code
                    </Trans>
                  ) : undefined
                }
              />
              <Button
                primary
                onClick={fetchConditionReport}
                loading={loading}
                disabled={!registrationNumber || alert === 'NoCode'}
                className="mt-[22px]"
              >
                <Trans id="mobility-item-insertion.form.condition-report.fetch.button">Fetch condition report</Trans>
              </Button>
            </div>
            <Alert type="warning" show={!registrationNumber}>
              <Trans id="mobility-item-insertion.form.condition-report.needs-regnr">
                <Button link onClick={focusRegistrationNumber}>
                  Registration number
                </Button>{' '}
                must be filled out to fetch condition report
              </Trans>
            </Alert>
            <Alert type="negative" show={alert === 'Error'}>
              <Trans id="mobility-item-insertion.form.condition-report.fetch.failed">
                Failed to fetch condition report
              </Trans>
            </Alert>
            <Alert type="warning" show={alert === 'NotFound'}>
              <Trans id="mobility-item-insertion.form.condition-report.not-found">Condition report not found</Trans>
            </Alert>
          </div>
        ) : (
          <Alert type="positive" show>
            <Trans id="mobility-item-insertion.form.condition-report.condition-report-attached">
              Condition reported is attached - change provider if you want to change the report
            </Trans>
          </Alert>
        ))}
    </>
  );
};
